import React, { useState, forwardRef, useRef, useImperativeHandle } from 'react'
import MaskedInput from 'react-text-mask'
import { useForm, Controller } from 'react-hook-form'
import { FaTimes } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

interface MyDateProps {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  className?: string
  required?: boolean
  icon?: React.ReactNode
  isInvalid?: boolean
}

const MyDate = forwardRef<HTMLInputElement, MyDateProps>((props, ref) => {
  const {
    label,
    value,
    onChange,
    error,
    className,
    required = false,
    icon,
    isInvalid = false,
  } = props
  const { t } = useTranslation()
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

  const { control } = useForm()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleClear = () => {
    onChange('')
    inputRef.current?.focus()
  }

  const getBackgroundColor = () => {
    if (isInvalid || error) return 'bg-redlight rounded-md'
    if (required && !value) return 'bg-redlight rounded-md'
    if (value) return 'bg-grisclair hover:bg-grisclair2 hover:rounded-md'
    return 'bg-grisclair hover:bg-grisclair2 hover:rounded-md'
  }

  return (
    <div
      className={`flex flex-col w-full relative rounded-md cursor-pointer ${className}`}
    >
      <div
        className={`relative flex w-full ${getBackgroundColor()} ${
          isFocused || value ? 'focused' : ''
        } ${isInvalid ? 'border-red-500' : ''}`}
      >
        {icon && (
          <div className='flex items-center px-3 text-anthracite bg-transparent rounded-l-md'>
            {icon}
          </div>
        )}
        <label
          htmlFor={`input-${label.replace(/\s+/g, '-').toLowerCase()}`}
          className={`transform transition-all duration-300 cursor-pointer ${
            isFocused || value
              ? 'absolute -translate-y-1/2 -translate-x-1/2 w-full text-center left-1/2 ml-0 font-semibold text-xs text-anthracite/60 rounded-t-md px-2 py-0 z-10'
              : `absolute inset-0 flex items-center ${
                  icon ? 'pl-10' : 'pl-2'
                } text-gray-400 text-sm`
          }`}
        >
          {t(label)}
          {required && ' *'}
        </label>

        <Controller
          name='date'
          control={control}
          defaultValue=''
          render={({ field }) => (
            <MaskedInput
              {...field}
              id={`input-${label.replace(/\s+/g, '-').toLowerCase()}`}
              mask={[/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
              placeholder={isFocused ? '__-__-____' : ''}
              placeholderChar={'_'}
              value={value}
              onChange={handleInputChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`w-full py-2.5 relative cursor-pointer px-3 bg-transparent text-sm border-none outline-none transition-all duration-300 ${
                icon ? 'pl-0' : ''
              } ${isInvalid ? 'bg-redlight' : ''}`}
              ref={(el: any) => {
                field.ref(el)
                if (el && el.inputElement) {
                  inputRef.current = el.inputElement
                }
              }}
            />
          )}
        />
        {value && (
          <button
            type='button'
            className='absolute right-2 top-1/2 transform -translate-y-1/2'
            onClick={handleClear}
          >
            <FaTimes className='text-gray-800 hover:text-redpink' />
          </button>
        )}
      </div>
      {error && (
        <span className='text-redpink absolute -bottom-2 left-8 text-xs mt-1 px-2 py-1 rounded'>
          {error}
        </span>
      )}
    </div>
  )
})

export default MyDate
